import { useEffect } from "react";

import { useOrderSummaryGraphKPILoading } from "@/atoms/order-page-atoms";
import { useUser } from "@components/user-context";
import { useQueryMetadata } from "@components/v2/modals";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchOrderSummaryGraphKPI,
  type FetchOrderSummaryGraphKPIVariables,
} from "@north-beam/nb-client-side";
import { MetricFormatters } from "@north-beam/nb-client-side/metrics";
import { metrics } from "@north-beam/nb-common";
import { useOrderGraphFilters } from "@pages/orders/use-orders-filters";
import { FETCH_ORDER_SUMMARY_GRAPH_KPI } from "@pages/settings/queries";

import { ChartLegend } from "./chart-legend";

export function OrdersMetrics() {
  const { dateRange, granularity, compareDateRange, ...restOfFilters } =
    useOrderGraphFilters();
  const { addQueryMetadata } = useQueryMetadata();

  const [, setOrderQueryLoading] = useOrderSummaryGraphKPILoading();
  const { user } = useUser();

  const { data: graphResponse, loading } = useNorthbeamQuery<
    FetchOrderSummaryGraphKPI,
    FetchOrderSummaryGraphKPIVariables
  >(FETCH_ORDER_SUMMARY_GRAPH_KPI, {
    variables: {
      filterOptions: restOfFilters,
      dateRange,
      comparedDateRange: compareDateRange,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Orders",
        topic: "Orders KPI Metrics",
        metadata: data.me.orderSummaryGraphKPI.queryMetadata,
      });
    },
  });

  const currentKPIs = graphResponse?.me?.orderSummaryGraphKPI.currentKPIs;
  const comparisonKPIs = graphResponse?.me?.orderSummaryGraphKPI.comparisonKPIs;

  useEffect(() => {
    setOrderQueryLoading(loading);
  }, [loading, setOrderQueryLoading]);

  return (
    <ChartLegend
      legendMetrics={[
        // Revenue metric
        {
          loading,
          currentValue: currentKPIs?.orderRevenue || 0,
          compareValue: comparisonKPIs?.orderRevenue || 0,
          name: "Revenue",
          isPositiveChangeGood: true,
          format: metrics.MetricTypes.CURRENCY,
          formatter: (v) =>
            MetricFormatters.currency(v, user.currencies.currentCurrency, {
              precision: "exact",
            }),
          hideComparisonFromTabDisplay: false,
        },
        // Revenue count metric
        {
          loading,
          currentValue: currentKPIs?.orderCount || 0,
          compareValue: comparisonKPIs?.orderCount || 0,
          name: "Order",
          isPositiveChangeGood: true,
          format: metrics.MetricTypes.INTEGER,
          formatter: (v) =>
            MetricFormatters.integer(v, user.currencies.currentCurrency, {
              precision: "exact",
            }),
          hideComparisonFromTabDisplay: false,
        },
      ]}
      selectMetric={(metric) => metric}
      selectedMetricIndices={[0, 1]}
    />
  );
}
